/* HelpDrawerPanel — iter-2.40
 *
 * Admin editor for the ContextHelpDrawer per-panel tips.
 * Changes are saved to MongoDB via PUT /api/admin/help/drawer-panels/{key}
 * and reflected in the drawer the next time it is opened (no redeploy).
 *
 * Data model: {sections:[{title,tips:[str]}], nextStep:{panel,reason}}
 * Falls back to hardcoded PANEL_TIPS in ContextHelpDrawer.jsx if a panel
 * has never been saved to the DB.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Save, Trash2, RotateCcw, ArrowUp, ArrowDown,
  Eye, EyeOff, ChevronRight, Loader2, Lightbulb, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { helpAdminApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PANEL_TIPS, ALWAYS_NEXT_TIP } from '@/lib/helpDrawerDefaults';

/* ── Panel catalogue (mirrors ContextHelpDrawer PANEL_META) ── */
const DRAWER_PANELS = [
  { key: 'home',       label: 'Getting Started'      },
  { key: 'editor',     label: 'Manuscript Editor'     },
  { key: 'chapters',   label: 'Chapters Panel'        },
  { key: 'bible',      label: 'Character Bible'       },
  { key: 'arc',        label: 'Arc Visualizer'        },
  { key: 'analysis',   label: 'Analysis'              },
  { key: 'chat',       label: 'AI Chat'               },
  { key: 'marketing',  label: 'Marketing & Business'  },
  { key: 'modes',      label: 'Editing Modes'         },
  { key: 'variables',  label: 'Project Variables'     },
  { key: 'bookshelf',  label: 'My Bookshelf'          },
  { key: 'word_count', label: 'Word Count'            },
  { key: 'narration',  label: 'Narration Studio'      },
  { key: 'offline',    label: 'Offline Audio'         },
  { key: 'tiers',      label: 'Subscriptions'         },
  { key: 'coach',      label: 'Live Coach'            },
];

function emptyPanel() {
  return { sections: [], nextStep: { panel: '', reason: '' } };
}

/* ── Tiny preview of one section (mirrors the drawer's TipSection) ── */
function SectionPreview({ section }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center gap-2 bg-muted/40 px-3 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {section.title || '(no title)'}
        </span>
      </div>
      <ul className="divide-y divide-border">
        {(section.tips || []).map((tip, i) => (
          <li key={i} className="flex gap-2 px-3 py-2">
            <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
            <span className="text-[11px] leading-relaxed text-foreground">{tip}</span>
          </li>
        ))}
        {(section.tips || []).length === 0 && (
          <li className="px-3 py-2 text-[11px] text-muted-foreground italic">No tips yet.</li>
        )}
      </ul>
    </div>
  );
}

/* ── Section editor row ── */
function SectionEditor({ section, idx, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  const updateTip = (ti, val) => {
    const tips = [...(section.tips || [])];
    tips[ti] = val;
    onChange({ ...section, tips });
  };
  const removeTip = (ti) => {
    const tips = (section.tips || []).filter((_, j) => j !== ti);
    onChange({ ...section, tips });
  };
  const addTip = () => {
    const tips = [...(section.tips || []), ''];
    onChange({ ...section, tips });
  };

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden" data-testid={`drawer-section-${idx}`}>
      {/* Section header */}
      <div className="flex items-center justify-between gap-2 bg-muted/40 px-3 py-2">
        <Input
          value={section.title}
          onChange={e => onChange({ ...section, title: e.target.value })}
          placeholder="Section title"
          className="h-7 text-xs flex-1 min-w-0"
          data-testid={`drawer-section-title-${idx}`}
        />
        <div className="flex items-center gap-0.5 shrink-0">
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onMoveUp} disabled={idx === 0} title="Move up">
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onMoveDown} disabled={idx === total - 1} title="Move down">
            <ArrowDown className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={onRemove} title="Remove section">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Tips list */}
      <div className="p-3 space-y-2">
        {(section.tips || []).map((tip, ti) => (
          <div key={ti} className="flex gap-1.5 items-start">
            <Lightbulb className="mt-2.5 h-3 w-3 shrink-0 text-amber-500" />
            <Textarea
              rows={2}
              value={tip}
              onChange={e => updateTip(ti, e.target.value)}
              placeholder="Tip text. Use [label](panel_key) to insert a navigation link chip."
              className="text-xs flex-1"
              data-testid={`drawer-tip-${idx}-${ti}`}
            />
            <Button size="icon" variant="ghost" className="h-7 w-7 mt-0.5 text-destructive shrink-0" onClick={() => removeTip(ti)}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        {(section.tips || []).length === 0 && (
          <p className="text-[11px] text-muted-foreground italic px-1">No tips — click Add Tip.</p>
        )}
        <Button size="sm" variant="outline" className="h-7 gap-1 mt-1" onClick={addTip} data-testid={`drawer-add-tip-${idx}`}>
          <Plus className="h-3 w-3" /> Add tip
        </Button>
      </div>
    </div>
  );
}

/* ── Main component ── */
export default function HelpDrawerPanel() {
  const [dbPanels, setDbPanels] = useState({});       // panels saved in DB
  const [activeKey, setActiveKey] = useState(DRAWER_PANELS[0].key);
  const [draft, setDraft] = useState(emptyPanel());   // current edit state
  const [showPreview, setShowPreview] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seedProgress, setSeedProgress] = useState({ done: 0, total: 0 });

  /* load saved panels */
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await helpAdminApi.getDrawerPanels();
      setDbPanels(data || {});
    } catch {
      toast.error('Failed to load drawer panels.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* sync draft when active panel or dbPanels changes */
  useEffect(() => {
    const saved = dbPanels[activeKey];
    setDraft(saved
      ? { sections: saved.sections || [], nextStep: saved.nextStep || { panel: '', reason: '' } }
      : emptyPanel()
    );
  }, [activeKey, dbPanels]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(
    dbPanels[activeKey]
      ? { sections: dbPanels[activeKey].sections || [], nextStep: dbPanels[activeKey].nextStep || { panel: '', reason: '' } }
      : emptyPanel()
  );

  const isInDb = Boolean(dbPanels[activeKey]);

  /* section mutations */
  const updateSection = (i, patch) => {
    const sections = [...draft.sections];
    sections[i] = patch;
    setDraft({ ...draft, sections });
  };
  const removeSection = (i) => setDraft({ ...draft, sections: draft.sections.filter((_, j) => j !== i) });
  const addSection = () => setDraft({ ...draft, sections: [...draft.sections, { title: 'New section', tips: [] }] });
  const moveSection = (i, dir) => {
    const sections = [...draft.sections];
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    [sections[i], sections[j]] = [sections[j], sections[i]];
    setDraft({ ...draft, sections });
  };

  /* save */
  const save = async () => {
    setBusy(true);
    try {
      await helpAdminApi.upsertDrawerPanel(activeKey, draft);
      toast.success(`"${DRAWER_PANELS.find(p => p.key === activeKey)?.label}" saved to database.`);
      setDbPanels(prev => ({ ...prev, [activeKey]: { ...draft, panel_key: activeKey } }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  /* revert to code default (delete from DB) */
  const revert = async () => {
    setBusy(true);
    try {
      await helpAdminApi.deleteDrawerPanel(activeKey);
      toast.success('Reverted to code default.');
      setDbPanels(prev => { const n = { ...prev }; delete n[activeKey]; return n; });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revert failed.');
    } finally {
      setBusy(false);
    }
  };

  /* seed all 16 panels from code defaults */
  const seedFromDefaults = async () => {
    setSeedDialogOpen(false);
    setBusy(true);
    setSeedProgress({ done: 0, total: DRAWER_PANELS.length });
    let count = 0;
    for (const { key } of DRAWER_PANELS) {
      const def = PANEL_TIPS[key];
      try {
        await helpAdminApi.upsertDrawerPanel(key, {
          sections: def?.sections || [],
          nextStep: def?.nextStep || ALWAYS_NEXT_TIP[key] || { panel: '', reason: '' },
        });
        count++;
      } catch (err) {
        console.error(`Seed failed for ${key}:`, err);
      }
      setSeedProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    toast.success(`Seeded ${count} of ${DRAWER_PANELS.length} panels from code defaults.`);
    await reload();
    setBusy(false);
    setSeedProgress({ done: 0, total: 0 });
  };

  /* reset ALL */
  const resetAll = async () => {
    setBusy(true);
    try {
      await helpAdminApi.resetDrawerPanels();
      toast.success('All drawer panels reset to code defaults.');
      setDbPanels({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  const activeLabel = DRAWER_PANELS.find(p => p.key === activeKey)?.label ?? activeKey;

  return (
    <div className="space-y-3" data-testid="help-drawer-editor">

      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-muted-foreground leading-snug max-w-lg">
          Changes save to MongoDB and appear in the drawer the next time it is opened — no redeploy needed.
          Panels not saved here fall back to code defaults automatically.
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm" variant="ghost"
            className="gap-1.5 text-[11.5px]"
            onClick={() => setShowPreview(v => !v)}
            data-testid="drawer-toggle-preview"
          >
            {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showPreview ? 'Hide preview' : 'Show preview'}
          </Button>
          {/* Seed from code defaults */}
          <AlertDialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-[11.5px]"
                disabled={busy}
                data-testid="drawer-seed-btn"
              >
                {busy && seedProgress.total > 0 ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Seeding {seedProgress.done}/{seedProgress.total}…
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3.5 w-3.5" />
                    Initialize from code defaults
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Initialize all 16 panels from code defaults?</AlertDialogTitle>
                <AlertDialogDescription>
                  This seeds all 16 drawer panels into the database using the current hardcoded content as a starting point.
                  Any panels already saved will be overwritten.
                  You can then edit individual panels and save your changes — use "Revert to code default" to undo changes per panel.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={seedFromDefaults} data-testid="drawer-seed-confirm">
                  Initialize all 16 panels
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {/* Reset all to defaults */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="gap-1.5 text-destructive text-[11.5px]" data-testid="drawer-reset-all-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Reset all to defaults
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset all drawer panels?</AlertDialogTitle>
                <AlertDialogDescription>
                  All saved drawer-panel overrides will be deleted. The ContextHelpDrawer will revert to its hardcoded code defaults. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetAll} disabled={busy} data-testid="drawer-reset-all-confirm">Reset all</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* Panel list */}
        <div className="col-span-12 sm:col-span-3 rounded-md border border-border bg-card p-2">
          <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Panels</p>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : (
            DRAWER_PANELS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveKey(key)}
                data-testid={`drawer-panel-item-${key}`}
                className={cn(
                  'w-full text-left px-2 py-1.5 rounded-sm text-[12px] flex items-center justify-between gap-2 transition-colors',
                  activeKey === key
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-foreground hover:bg-secondary',
                )}
              >
                <span className="flex-1 truncate">{label}</span>
                {dbPanels[key]
                  ? <Badge variant="secondary" className="text-[9px] py-0 px-1.5 shrink-0">saved</Badge>
                  : <span className="text-[9px] text-muted-foreground/60 shrink-0">default</span>}
                <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            ))
          )}
        </div>

        {/* Editor + preview */}
        <div className={cn('col-span-12', showPreview ? 'sm:col-span-9' : 'sm:col-span-9')}>
          <div className={cn('grid gap-3', showPreview ? 'grid-cols-2' : 'grid-cols-1')}>

            {/* Editor column */}
            <div className="space-y-3 rounded-md border border-border bg-card p-3" data-testid="drawer-section-editor">
              <div className="flex items-center justify-between gap-2 pb-1 border-b border-border">
                <div>
                  <p className="text-[13px] font-semibold">{activeLabel}</p>
                  <p className="text-[10.5px] text-muted-foreground">
                    {isInDb
                      ? <>Override saved{dbPanels[activeKey]?.updated_at ? ` · ${new Date(dbPanels[activeKey].updated_at).toLocaleString()}` : ''}</>
                      : 'Using code default — save to override'}
                  </p>
                </div>
                {isDirty && (
                  <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">Unsaved changes</Badge>
                )}
              </div>

              {/* Next Step */}
              <div className="rounded-md border border-dashed border-border p-3 space-y-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Next Step</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10.5px]">Target panel key</Label>
                    <Input
                      value={draft.nextStep?.panel ?? ''}
                      onChange={e => setDraft({ ...draft, nextStep: { ...draft.nextStep, panel: e.target.value } })}
                      placeholder="e.g. analysis"
                      className="h-7 text-xs"
                      list="drawer-panel-keys"
                      data-testid="drawer-nextstep-panel"
                    />
                    <datalist id="drawer-panel-keys">
                      {DRAWER_PANELS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </datalist>
                  </div>
                  <div>
                    <Label className="text-[10.5px]">Reason shown to user</Label>
                    <Input
                      value={draft.nextStep?.reason ?? ''}
                      onChange={e => setDraft({ ...draft, nextStep: { ...draft.nextStep, reason: e.target.value } })}
                      placeholder="e.g. Run analysis after expanding."
                      className="h-7 text-xs"
                      data-testid="drawer-nextstep-reason"
                    />
                  </div>
                </div>
              </div>

              {/* Sections */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px]">Sections ({draft.sections.length})</Label>
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={addSection} data-testid="drawer-add-section">
                    <Plus className="h-3 w-3" /> Add section
                  </Button>
                </div>
                {draft.sections.length === 0 && (
                  <div className="rounded-md border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
                    No sections yet — click "Add section" to start, or leave empty to use the code default.
                  </div>
                )}
                {draft.sections.map((sec, i) => (
                  <SectionEditor
                    key={i}
                    section={sec}
                    idx={i}
                    total={draft.sections.length}
                    onChange={patch => updateSection(i, patch)}
                    onRemove={() => removeSection(i)}
                    onMoveUp={() => moveSection(i, -1)}
                    onMoveDown={() => moveSection(i, 1)}
                  />
                ))}
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-between pt-2 border-t border-border gap-2 flex-wrap">
                {isInDb ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-destructive gap-1.5 text-[11.5px]" data-testid="drawer-revert-btn">
                        <RotateCcw className="h-3.5 w-3.5" /> Revert to code default
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revert "{activeLabel}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The saved override for this panel will be deleted. The ContextHelpDrawer will fall back to the code default for this panel.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={revert} disabled={busy} data-testid="drawer-revert-confirm">Revert</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : <div />}
                <Button
                  size="sm"
                  onClick={save}
                  disabled={busy || !isDirty}
                  className="gap-1.5 text-[11.5px]"
                  data-testid="drawer-save-panel-btn"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save to database
                </Button>
              </div>
            </div>

            {/* Live preview column */}
            {showPreview && (
              <div className="rounded-md border border-border bg-card p-3 space-y-2 max-h-[70vh] overflow-y-auto">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide pb-1 border-b border-border">
                  Live preview — "{activeLabel}"
                </p>
                {draft.sections.length === 0 ? (
                  <p className="text-[11.5px] text-muted-foreground italic py-4 text-center">
                    Add sections on the left to see a preview.
                  </p>
                ) : (
                  <>
                    {draft.sections.map((sec, i) => (
                      <SectionPreview key={i} section={sec} />
                    ))}
                    {draft.nextStep?.panel && (
                      <div className="mt-3 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Next step</div>
                          <div className="text-[12px] font-medium">{draft.nextStep.panel}</div>
                          <div className="text-[10.5px] text-muted-foreground">{draft.nextStep.reason}</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
